'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ShipmentDetailsProps {
  trackingNumber: string;
  status: string;
  estimatedDelivery: string;
  origin: string;
  destination: string;
  weight: string;
  dimensions: string;
}

export default function ShipmentDetails({
  trackingNumber,
  status,
  estimatedDelivery,
  origin,
  destination,
  weight,
  dimensions,
}: ShipmentDetailsProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'in transit':
        return 'bg-warning/10 text-warning';
      case 'pending':
        return 'bg-muted text-muted-foreground';
      case 'delayed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Shipment Details</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Tracking #:</span>
            <span className="text-sm font-mono font-medium text-foreground">{trackingNumber}</span>
            <button
              onClick={handleCopy}
              className="p-1 hover:bg-muted rounded transition-colors"
              title="Copy tracking number"
            >
              <Icon 
                name={copied ? 'CheckIcon' : 'ClipboardDocumentIcon'} 
                size={16} 
                className={copied ? 'text-success' : 'text-muted-foreground'} 
              />
            </button>
          </div>
        </div>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(status)}`}>
          {status}
        </span>
      </div>

      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="CalendarIcon" size={16} />
              <span>Estimated Delivery</span>
            </div>
            <p className="text-foreground font-medium">{estimatedDelivery}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="MapPinIcon" size={16} />
              <span>Origin</span>
            </div>
            <p className="text-foreground font-medium">{origin}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="MapPinIcon" size={16} />
              <span>Destination</span>
            </div>
            <p className="text-foreground font-medium">{destination}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="ScaleIcon" size={16} />
              <span>Weight</span>
            </div>
            <p className="text-foreground font-medium">{weight}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="CubeIcon" size={16} />
              <span>Dimensions</span>
            </div>
            <p className="text-foreground font-medium">{dimensions}</p>
          </div>
        </div>
      </div>
    </div>
  );
}